import { usePaymentLanguage } from '../../lib/paymentLanguage';
import React, { useEffect, useRef } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { PaymentDeadline, usePaymentDeadline } from '../../hooks/usePaymentDeadline';

interface CountdownTimerProps extends PaymentDeadline {
    onExpired?: () => void;
    className?: string;
    compact?: boolean;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ onExpired, className = '', compact = false, ...timing }) => {
    const { t } = usePaymentLanguage();
    const { deadline, remaining, expired: isExpired } = usePaymentDeadline(timing);
    const notified = useRef<number | null>(null);
    const callback = useRef(onExpired);
    callback.current = onExpired;
    useEffect(() => {
        if (isExpired && notified.current !== deadline) {
            notified.current = deadline;
            callback.current?.();
        }
    }, [deadline, isExpired]);
    const seconds = Number.isFinite(remaining) ? Math.ceil(remaining / 1000) : 0;
    const timeLeft = { hours: Math.floor(seconds / 3600), minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60, total: remaining };

    if (isExpired) {
        return (
            <div className={`flex items-center gap-2 text-red-600 dark:text-red-300 ${className}`}>
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">{t("Время истекло")}</span>
            </div>
        );
    }

    const isWarning = timeLeft.total <= 5 * 60 * 1000; // 5 минут
    const isCritical = timeLeft.total <= 2 * 60 * 1000; // 2 минуты

    const textColor = isCritical
        ? 'text-red-600 dark:text-red-300'
        : isWarning
            ? 'text-orange-600 dark:text-orange-300'
            : 'text-green-600 dark:text-green-300';

    const bgColor = isCritical
        ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
        : isWarning
            ? 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800'
            : 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800';

    return (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-md ${compact ? 'text-sm' : `border ${bgColor}`} ${textColor} ${className}`}>
            <Clock className="h-4 w-4" />
            <span className={compact ? 'sr-only' : 'font-medium'}>
                {t("Осталось времени:")}
            </span>
            <span className={`font-mono font-bold ${compact ? 'text-sm' : 'text-lg'}`}>
                {timeLeft.hours > 0 && `${String(timeLeft.hours).padStart(2, '0')}:`}
                {String(timeLeft.minutes).padStart(2, '0')}:
                {String(timeLeft.seconds).padStart(2, '0')}
            </span>
            {isCritical && !compact && (
                <span className="text-xs animate-pulse">
                    {t("Срочно!")}
                </span>
            )}
        </div>
    );
};
